import Cookies from "js-cookie";
import { Amplify } from "aws-amplify";
import { Hub } from "aws-amplify/utils";
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from "aws-amplify/auth";
import { currentUser, currentUserAttributes } from "../store";
import { identifyAndTrackPageView, trackSignupProvider } from "./hubspot";
import { trackSignup as trackSignupLinkedIn } from "./linkedin";
import { trackSignup } from "./ga";
import type { Error } from "../components/react/hooks/useAuthErrors";
import { clearPostLoginRedirect, getPostLoginRedirect } from "./storage";
import { logger } from "./logger";
import { isBrowser } from "./ssr";
import { getBroadcastChannel, LOGOUT_ALL_ACTION } from "./broadcastChannel";

const redirectSignIn = isBrowser()
  ? [window.location.protocol + "//" + window.location.host + "/auth/callback/"]
  : [];

const redirectSignOut = isBrowser()
  ? [window.location.protocol + "//" + window.location.host + "/login/"]
  : [];

export const initAuth = () => {
  logger("Configuring Amplify");
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolClientId: import.meta.env.PUBLIC_USER_POOL_CLIENT_ID,
        userPoolId: import.meta.env.PUBLIC_USER_POOL_ID,
        loginWith: {
          oauth: {
            domain: import.meta.env.PUBLIC_COGNITO_OAUTH_DOMAIN,
            scopes: [
              "email",
              "profile",
              "openid",
              "aws.cognito.signin.user.admin",
            ],
            responseType: "code",
            redirectSignIn,
            redirectSignOut,
          },
        },
      },
    },
  });
  if (isBrowser()) {
    getBroadcastChannel().addEventListener("message", (event) => {
      if (event.data === LOGOUT_ALL_ACTION) {
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    });
  }
};
initAuth();

logger('Hub listening to "auth" event');
Hub.listen("auth", async ({ payload }) => {
  switch (payload.event) {
    case "signInWithRedirect":
      logger('Hub received event "signInWithRedirect"', payload);
      try {
        const user = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        const authSession = await fetchAuthSession();

        currentUser.set(user);
        currentUserAttributes.set(userAttributes);

        if (userAttributes.email) {
          logger("Identifying and tracking page view with Hubspot");
          identifyAndTrackPageView(userAttributes.email);
        }

        let isSignup =
          authSession.tokens?.idToken?.payload?.first_login === "true";
        let gaSignupTrackingPromise = Promise.resolve();
        let hsSignupTrackingPromise = Promise.resolve();
        if (isSignup) {
          const provider = userAttributes["custom:signup_provider"];
          if (provider) {
            gaSignupTrackingPromise = trackSignup(provider);
            hsSignupTrackingPromise = trackSignupProvider(provider);
          }
          trackSignupLinkedIn();
        }

        const hubspotFinishedPromise = new Promise<void>((resolve) => {
          // Checks if hubspot has finished sending off tracking events
          const interval = window.setInterval(() => {
            logger("[HUBSPOT] Checking if hubspot tracking finished");
            if (!window._hsq.length) {
              window.clearInterval(interval);
              logger("[HUBSPOT] Hubspot tracking finished");
              resolve();
            }
          }, 100);
        });
        const trackingFinishedTimeout = new Promise<void>((resolve) => {
          // Fallback in case hubspot doesn't finish sending off tracking events
          window.setTimeout(() => {
            logger("TRACKING TIMEOUT");
            resolve();
          }, 5000);
        });

        const results = await Promise.race([
          Promise.allSettled([
            gaSignupTrackingPromise,
            hsSignupTrackingPromise,
            hubspotFinishedPromise,
          ]),
          trackingFinishedTimeout,
        ]);
        logger("Tracking results", results);
      } catch (error) {
        console.error("Post federated auth code failure", error);
      }

      const redirect = getPostLoginRedirect();
      if (redirect) {
        clearPostLoginRedirect();
      }
      window.history.replaceState({}, "", redirect ?? "/app/");
      window.location.reload();

      break;
    case "signInWithRedirect_failure": {
      logger('Hub received event "signInWithRedirect_failure"', payload);
      const redirect = `/login/?federatedSignInError=${encodeURIComponent(payload.data.error?.name ?? "unknown")}&errorDescription=${encodeURIComponent(payload.data.error?.message ?? "unknown")}`;
      window.history.replaceState({}, "", redirect);
      window.location.reload();
      break;
    }

    case "signedOut": {
      logger('Hub received event "signedOut"', payload);

      currentUser.set(null);
      currentUserAttributes.set(null);

      Cookies.remove("uid");
      Cookies.remove("token");
      Cookies.remove("user_info");

      getBroadcastChannel().postMessage(LOGOUT_ALL_ACTION);
      break;
    }
  }
});

export const getUserFriendlyFederatedSignInError = (
  type?: string,
  message?: string,
) => {
  const baseError: Error = {
    id: "FEDERATED_SIGN_IN_ERROR",
    input: "general",
    message: `Unable to sign up with your social provider. Unknown error. ${message ?? ""}`,
  };

  if (type === "OAuthSignInException") {
    if (message === "attributes required: [given_name]") {
      return {
        ...baseError,
        message:
          "Unable to sign up with your social provider. Your first name is required to sign up to VideoScribe. Please set your first name in your social provider account settings and try again.",
      };
    }
    if (message === "attributes required: [family_name]") {
      return {
        ...baseError,
        message:
          "Unable to sign up with your social provider. Your last name is required to sign up to VideoScribe. Please set your last name in your social provider account settings and try again.",
      };
    }
  }
  return baseError;
};

export {
  getCurrentUser,
  fetchUserAttributes,
  signInWithRedirect,
  signOut,
  signUp,
  resendSignUpCode,
  confirmSignUp,
} from "@aws-amplify/auth";
